import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Chip, Alert,
    CircularProgress, TextField, IconButton, Tooltip, Paper,
    Divider, Stack, useMediaQuery, useTheme,
} from '@mui/material';
import {
    Search, Refresh, MergeType, PersonSearch,
    CheckCircle, Block,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DuplicateCandidate, Customer, customerMergeService } from '../../services/customerMergeService';
import { CustomerMergeModal } from '../../components/common/CustomerMergeModal';
import { CustomerSearchAutocomplete } from '../../components/common/CustomerSearchAutocomplete';
import { Customer as AppCustomer } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';

// ─── Duplicate detection section ────────────────────────────────────────────

const StatusColor: Record<string, 'warning' | 'success' | 'default' | 'info'> = {
    PENDING: 'warning',
    MERGED: 'success',
    DISMISSED: 'default',
    REVIEWED: 'info',
};

// ─── Main component ──────────────────────────────────────────────────────────

/** One side of a pair, as shown on a phone card. */
const PairSide: React.FC<{ label: string; name?: string | null; id: string; phone?: string | null; email?: string | null }> = ({ label, name, id, phone, email }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {name || id.substring(0, 8) + '…'}
        </Typography>
        {phone && <Typography variant="caption" color="text.secondary" display="block">{phone}</Typography>}
        {email && <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>{email}</Typography>}
    </Box>
);

export const DuplicateCustomers: React.FC = () => {
    const { canWrite } = usePermissions();
    const theme = useTheme();
    // Phones get cards — the grid needs far more width than a phone has.
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Duplicate list state
    const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('PENDING');
    const [minScore, setMinScore] = useState(0.7);

    // Merge modal state
    const [mergeOpen, setMergeOpen] = useState(false);
    const [mergeCustomer1, setMergeCustomer1] = useState<Customer | null>(null);
    const [mergeCustomer2, setMergeCustomer2] = useState<Customer | null>(null);
    const [mergeCandidate, setMergeCandidate] = useState<DuplicateCandidate | undefined>(undefined);
    const [loadingPair, setLoadingPair] = useState<string | null>(null);

    // Arbitrary merge state
    const [arbCustomer1, setArbCustomer1] = useState<AppCustomer | null>(null);
    const [arbCustomer2, setArbCustomer2] = useState<AppCustomer | null>(null);

    const loadCandidates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await customerMergeService.getDuplicateCandidates({
                status: statusFilter === 'ALL' ? undefined : statusFilter,
                min_score: minScore,
                limit: 100,
            });
            setCandidates(data);
        } catch {
            setError('Failed to load duplicate candidates');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, minScore]);

    useEffect(() => { loadCandidates(); }, [loadCandidates]);

    const handleScan = async () => {
        setScanning(true);
        setError(null);
        try {
            const result = await customerMergeService.scanForDuplicates(minScore);
            setSuccess(result.message);
            await loadCandidates();
        } catch {
            setError('Scan failed');
        } finally {
            setScanning(false);
        }
    };

    const handleOpenMerge = async (candidate: DuplicateCandidate) => {
        setLoadingPair(candidate.candidate_id);
        try {
            const [c1, c2] = await Promise.all([
                customerMergeService.getCustomerWithDetails(candidate.customer1_id),
                customerMergeService.getCustomerWithDetails(candidate.customer2_id),
            ]);
            setMergeCustomer1(c1);
            setMergeCustomer2(c2);
            setMergeCandidate(candidate);
            setMergeOpen(true);
        } catch {
            setError('Failed to load customer details');
        } finally {
            setLoadingPair(null);
        }
    };

    const handleDismiss = async (candidateId: string) => {
        try {
            await customerMergeService.dismissDuplicateCandidate(candidateId, {
                notes: 'Dismissed from duplicate management',
            });
            await loadCandidates();
        } catch {
            setError('Failed to dismiss candidate');
        }
    };

    const handleMergeSuccess = async (merged: Customer) => {
        const name = [merged.first_name, merged.last_name].filter(Boolean).join(' ');
        setSuccess(`Customers successfully merged into "${name}"`);
        setMergeOpen(false);
        setMergeCustomer1(null);
        setMergeCustomer2(null);
        setMergeCandidate(undefined);
        await loadCandidates();
    };

    // Arbitrary merge: open modal with two searched customers
    const handleArbMerge = async () => {
        if (!arbCustomer1 || !arbCustomer2) return;
        if (arbCustomer1.customer_id === arbCustomer2.customer_id) {
            setError('Please select two different customers');
            return;
        }
        setLoadingPair('arb');
        try {
            const [c1, c2] = await Promise.all([
                customerMergeService.getCustomerWithDetails(arbCustomer1.customer_id),
                customerMergeService.getCustomerWithDetails(arbCustomer2.customer_id),
            ]);
            setMergeCustomer1(c1);
            setMergeCustomer2(c2);
            setMergeCandidate(undefined); // no duplicate candidate record
            setMergeOpen(true);
        } catch {
            setError('Failed to load customer details');
        } finally {
            setLoadingPair(null);
        }
    };

    const counts = {
        pending: candidates.filter(c => c.status === 'PENDING').length,
        merged: candidates.filter(c => c.status === 'MERGED').length,
        dismissed: candidates.filter(c => c.status === 'DISMISSED').length,
    };

    const columns: GridColDef[] = [
        {
            field: 'match_tier',
            headerName: 'Class',
            width: 70,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => {
                const info = customerMergeService.getTierInfo(params.row.match_tier);
                return (
                    <Tooltip title={info.tooltip}>
                        <Chip
                            label={info.label}
                            color={info.color}
                            size="small"
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: 'match_score',
            headerName: 'Score',
            width: 90,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => {
                const pct = Math.round(params.row.match_score * 100);
                return (
                    <Chip
                        label={`${pct}%`}
                        color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'info'}
                        size="small" variant="outlined"
                    />
                );
            },
        },
        {
            field: 'customer1_name',
            headerName: 'Customer A',
            flex: 1,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>
                        {params.row.customer1_name || params.row.customer1_id.substring(0, 8) + '…'}
                    </Typography>
                    {params.row.customer1_phone && (
                        <Typography variant="caption" color="text.secondary">
                            {params.row.customer1_phone}
                        </Typography>
                    )}
                    {params.row.customer1_email && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: params.row.customer1_phone ? 1 : 0 }}>
                            {params.row.customer1_email}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'customer2_name',
            headerName: 'Customer B',
            flex: 1,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>
                        {params.row.customer2_name || params.row.customer2_id.substring(0, 8) + '…'}
                    </Typography>
                    {params.row.customer2_phone && (
                        <Typography variant="caption" color="text.secondary">
                            {params.row.customer2_phone}
                        </Typography>
                    )}
                    {params.row.customer2_email && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: params.row.customer2_phone ? 1 : 0 }}>
                            {params.row.customer2_email}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'match_criteria',
            headerName: 'Why flagged',
            width: 200,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => {
                const criteria = customerMergeService.formatMatchCriteria(params.row.match_criteria);
                return (
                    <Box>
                        {criteria.slice(0, 2).map((c, i) => (
                            <Typography key={i} variant="caption" display="block">{c}</Typography>
                        ))}
                        {criteria.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                                +{criteria.length - 2} more
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 110,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => (
                <Chip
                    label={params.row.status}
                    color={StatusColor[params.row.status] ?? 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 110,
            sortable: false,
            renderCell: (params: GridRenderCellParams<DuplicateCandidate>) => (
                canWrite ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Review & Merge">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenMerge(params.row)}
                                    disabled={params.row.status === 'MERGED' || loadingPair === params.row.candidate_id}
                                    color="primary"
                                >
                                    {loadingPair === params.row.candidate_id
                                        ? <CircularProgress size={16} />
                                        : <MergeType fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Not a duplicate — dismiss">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDismiss(params.row.candidate_id)}
                                    disabled={params.row.status !== 'PENDING'}
                                    color="default"
                                >
                                    <Block fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ) : null
            ),
        },
    ];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
                    <Typography variant="h5" fontWeight={700}>Duplicate Customer Management</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Detect and merge duplicate customers to keep your records clean
                    </Typography>
                </Box>
                {canWrite && (
                    <Button
                        variant="contained"
                        startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <Search />}
                        onClick={handleScan}
                        disabled={scanning || loading}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {scanning ? 'Scanning…' : 'Scan for Duplicates'}
                    </Button>
                )}
            </Box>

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}
                    icon={<CheckCircle />}>{success}</Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {/* ── ARBITRARY MERGE SECTION ─────────────────────────────── */}
            {canWrite && (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PersonSearch color="primary" />
                        <Typography variant="subtitle1" fontWeight={700}>
                            Merge Any Two Customers
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Search and merge any two customers — even if they haven't been flagged as duplicates. All orders from both accounts will be transferred to the surviving account.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 240 }}>
                            <CustomerSearchAutocomplete
                                value={arbCustomer1}
                                onChange={setArbCustomer1}
                                label="Customer A"
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                            <MergeType color="action" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 240 }}>
                            <CustomerSearchAutocomplete
                                value={arbCustomer2}
                                onChange={setArbCustomer2}
                                label="Customer B"
                            />
                        </Box>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={loadingPair === 'arb' ? <CircularProgress size={18} color="inherit" /> : <MergeType />}
                            onClick={handleArbMerge}
                            disabled={!arbCustomer1 || !arbCustomer2 || loadingPair === 'arb'}
                            sx={{ mt: 0.5, minWidth: 140 }}
                        >
                            Review & Merge
                        </Button>
                    </Box>
                </CardContent>
            </Card>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* ── DUPLICATE CANDIDATES SECTION ────────────────────────── */}
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
                Detected Duplicate Candidates
            </Typography>

            {/* Filters */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: '12px !important' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Status filter chips */}
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {['ALL', 'PENDING', 'MERGED', 'DISMISSED'].map(s => (
                                <Chip
                                    key={s}
                                    label={s === 'ALL' ? `All` : `${s} ${s === 'PENDING' ? `(${counts.pending})` : s === 'MERGED' ? `(${counts.merged})` : `(${counts.dismissed})`}`}
                                    color={statusFilter === s ? (StatusColor[s] ?? 'primary') : 'default'}
                                    variant={statusFilter === s ? 'filled' : 'outlined'}
                                    size="small"
                                    onClick={() => setStatusFilter(s)}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ))}
                        </Stack>

                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                label="Min. Match Score"
                                type="number"
                                size="small"
                                value={minScore}
                                onChange={(e) => setMinScore(Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)))}
                                inputProps={{ min: 0, max: 1, step: 0.1 }}
                                sx={{ width: 160 }}
                                helperText="0 = loose · 1 = exact"
                            />
                            <Tooltip title="Refresh">
                                <IconButton onClick={loadCandidates} disabled={loading}>
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Cards on a phone, grid elsewhere */}
            {isMobile ? (
                loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : candidates.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography color="text.secondary">
                            {statusFilter === 'PENDING'
                                ? 'No pending duplicates found. Tap "Scan for Duplicates" to search.'
                                : 'No records found'}
                        </Typography>
                    </Box>
                ) : (
                    candidates.map((c) => {
                        const tier = customerMergeService.getTierInfo(c.match_tier);
                        const pct = Math.round(c.match_score * 100);
                        const criteria = customerMergeService.formatMatchCriteria(c.match_criteria);
                        return (
                            <Card key={c.candidate_id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                                <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.5 } }}>
                                    <Stack spacing={1.25}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip label={tier.label} color={tier.color} size="small" />
                                            <Chip
                                                label={`${pct}% match`}
                                                color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'info'}
                                                size="small" variant="outlined"
                                            />
                                            <Box sx={{ flex: 1 }} />
                                            <Chip label={c.status} color={StatusColor[c.status] ?? 'default'} size="small" />
                                        </Stack>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'start' }}>
                                            <PairSide label="Customer A" name={c.customer1_name} id={c.customer1_id} phone={c.customer1_phone} email={c.customer1_email} />
                                            <MergeType color="action" fontSize="small" sx={{ mt: 2.5 }} />
                                            <PairSide label="Customer B" name={c.customer2_name} id={c.customer2_id} phone={c.customer2_phone} email={c.customer2_email} />
                                        </Box>
                                        {criteria.length > 0 && (
                                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, px: 1.25, py: 1 }}>
                                                <Typography variant="caption" color="text.secondary">Why flagged</Typography>
                                                {criteria.map((line, i) => (
                                                    <Typography key={i} variant="body2" sx={{ fontSize: 13 }}>{line}</Typography>
                                                ))}
                                            </Box>
                                        )}
                                        {canWrite && (
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={loadingPair === c.candidate_id ? <CircularProgress size={16} color="inherit" /> : undefined}
                                                    onClick={() => handleOpenMerge(c)}
                                                    disabled={c.status === 'MERGED' || loadingPair === c.candidate_id}
                                                    sx={{ flex: 1, whiteSpace: 'nowrap' }}
                                                >
                                                    Review & Merge
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="inherit"
                                                    onClick={() => handleDismiss(c.candidate_id)}
                                                    disabled={c.status !== 'PENDING'}
                                                    sx={{ flex: 1, whiteSpace: 'nowrap' }}
                                                >
                                                    Not a duplicate
                                                </Button>
                                            </Stack>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })
                )
            ) : (
            <Paper variant="outlined">
                <DataGrid
                    rows={candidates}
                    columns={columns}
                    getRowId={(row) => row.candidate_id}
                    loading={loading}
                    autoHeight
                    rowHeight={64}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                        // 'A' < 'B' < 'C' already sorts correctly as plain
                        // strings; the backend returns rows in this order
                        // by default too, this just keeps it explicit if a
                        // user reorders and comes back.
                        sorting: { sortModel: [{ field: 'match_tier', sort: 'asc' }, { field: 'match_score', sort: 'desc' }] },
                    }}
                    disableRowSelectionOnClick
                    slots={{
                        noRowsOverlay: () => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120 }}>
                                <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography color="text.secondary">
                                    {statusFilter === 'PENDING'
                                        ? 'No pending duplicates found. Click "Scan for Duplicates" to search.'
                                        : 'No records found'}
                                </Typography>
                            </Box>
                        ),
                    }}
                />
            </Paper>
            )}

            {/* Merge modal */}
            {mergeOpen && mergeCustomer1 && mergeCustomer2 && (
                <CustomerMergeModal
                    open={mergeOpen}
                    onClose={() => {
                        setMergeOpen(false);
                        setMergeCustomer1(null);
                        setMergeCustomer2(null);
                        setMergeCandidate(undefined);
                        setArbCustomer1(null);
                        setArbCustomer2(null);
                    }}
                    customer1={mergeCustomer1}
                    customer2={mergeCustomer2}
                    duplicateCandidate={mergeCandidate}
                    onMergeSuccess={handleMergeSuccess}
                />
            )}
        </Box>
    );
};
